import { useEffect, useRef } from "react";

type Props = {
  open: boolean;
  src: string;
  alt?: string;
  onClose: () => void;
  minScale?: number; // 기본 1 (부모에서 0.3~0.5 등으로 내리면 원본 이하 축소 허용)
  maxScale?: number; // 기본 6
};

export default function ZoomableImageModal({
  open,
  src,
  alt,
  onClose,
  minScale = 1,
  maxScale = 6,
}: Props) {
  const vpRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);

  // 내부 상태 (리렌더 없이 유지)
  const scaleRef = useRef(1);
  const naturalRef = useRef({ w: 0, h: 0 });

  // body 스크롤 잠금
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // !important 로 크기 강제
  const setDimImportant = (el: HTMLElement, w: number, h: number) => {
    el.style.setProperty("width", `${w}px`, "important");
    el.style.setProperty("height", `${h}px`, "important");
    // 전역 min-width/height 규칙에 대비
    el.style.setProperty("min-width", `0px`, "important");
    el.style.setProperty("min-height", `0px`, "important");
  };

  const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

  // 특정 좌표(cx,cy) 기준으로 확대하고 스크롤을 보정 (경계 클램핑 포함)
  const zoomAt = (cx: number, cy: number, nextScale: number) => {
    const vp = vpRef.current!;
    const { w, h } = naturalRef.current;

    const beforeW = w * scaleRef.current;
    const beforeH = h * scaleRef.current;

    const rect = vp.getBoundingClientRect();
    const contentX = vp.scrollLeft + (cx - rect.left);
    const contentY = vp.scrollTop + (cy - rect.top);

    const relX = beforeW ? contentX / beforeW : 0.5;
    const relY = beforeH ? contentY / beforeH : 0.5;

    scaleRef.current = clamp(nextScale, minScale, maxScale);
    setDimImportant(imgRef.current!, w * scaleRef.current, h * scaleRef.current);

    const afterW = w * scaleRef.current;
    const afterH = h * scaleRef.current;

    const idealScrollLeft = relX * afterW - (cx - rect.left);
    const idealScrollTop = relY * afterH - (cy - rect.top);

    const maxSL = Math.max(0, afterW - vp.clientWidth);
    const maxST = Math.max(0, afterH - vp.clientHeight);

    vp.scrollLeft = clamp(idealScrollLeft, 0, maxSL);
    vp.scrollTop = clamp(idealScrollTop, 0, maxST);
  };

  // 이미지 로드 → 원본 크기 + 중앙 정렬
  useEffect(() => {
    if (!open || !imgRef.current) return;
    const img = imgRef.current;

    const onload = () => {
      naturalRef.current = { w: img.naturalWidth, h: img.naturalHeight };
      scaleRef.current = 1;
      setDimImportant(img, img.naturalWidth, img.naturalHeight);
      requestAnimationFrame(() => {
        const vp = vpRef.current!;
        vp.scrollLeft = Math.max(0, (img.naturalWidth - vp.clientWidth) / 2);
        vp.scrollTop = Math.max(0, (img.naturalHeight - vp.clientHeight) / 2);
      });
    };

    if (img.complete && img.naturalWidth) onload();
    else img.addEventListener("load", onload, { once: true });
  }, [open, src]);

  // 제스처 (핀치/팬/휠/더블탭) — 전환 시점 재베이스 + 유령 클릭 억제 + 클램핑
  useEffect(() => {
    if (!open || !vpRef.current) return;
    const vp = vpRef.current;

    const pointers = new Map<number, { x: number; y: number }>();
    let prevDist: number | null = null;

    let isPanning = false;
    let pan = { x: 0, y: 0, sl: 0, st: 0 };

    let wasPinching = false;
    let lastPinchEnd = 0;   // 핀치 직후 click 무시용
    let lastInteract = 0;   // 모든 제스처의 최근 시각

    const onDown = (e: PointerEvent) => {
      vp.setPointerCapture?.(e.pointerId);
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      lastInteract = Date.now();

      if (pointers.size === 1) {
        // 싱글 시작 → 팬 기준 세팅
        isPanning = true;
        pan = { x: e.clientX, y: e.clientY, sl: vp.scrollLeft, st: vp.scrollTop };
      }
      if (pointers.size === 2) {
        // 핀치 시작
        wasPinching = true;
        prevDist = null;
        isPanning = false; // 핀치 중엔 팬 비활성화
      }
    };

    const onMove = (e: PointerEvent) => {
      e.preventDefault(); // 기본 제스처 차단 (안드/크롬)
      if (!pointers.has(e.pointerId)) return;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      lastInteract = Date.now();

      if (pointers.size >= 2) {
        const p = Array.from(pointers.values());
        const d = Math.hypot(p[0].x - p[1].x, p[0].y - p[1].y);
        if (prevDist == null) prevDist = d;
        // 약간의 감쇄를 주면 핀치-끝 전환 때 튐이 줄어듦
        const factor = Math.pow(d / prevDist, 0.98);
        prevDist = d;

        const cx = (p[0].x + p[1].x) / 2;
        const cy = (p[0].y + p[1].y) / 2;
        zoomAt(cx, cy, scaleRef.current * factor);
        return;
      }

      if (isPanning) {
        vp.scrollLeft = pan.sl - (e.clientX - pan.x);
        vp.scrollTop = pan.st - (e.clientY - pan.y);
      }
    };

    const onUp = (e: PointerEvent) => {
      pointers.delete(e.pointerId);
      lastInteract = Date.now();

      if (pointers.size === 1) {
        // 🔧 핀치가 막 끝났다면 남은 손가락으로 팬 기준 재설정 (점프 방지)
        const rem = Array.from(pointers.values())[0];
        isPanning = true;
        pan = { x: rem.x, y: rem.y, sl: vp.scrollLeft, st: vp.scrollTop };
        prevDist = null;

        if (wasPinching) {
          lastPinchEnd = Date.now();
          wasPinching = false;
        }
        return;
      }

      if (pointers.size === 0) {
        isPanning = false;
        prevDist = null;
        if (wasPinching) {
          lastPinchEnd = Date.now();
          wasPinching = false;
        }
      }
    };

    // 트랙패드/Ctrl+wheel 줌
    const onWheel = (e: WheelEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      lastInteract = Date.now();
      const factor = e.deltaY < 0 ? 1.1 : 0.9;
      zoomAt(e.clientX, e.clientY, scaleRef.current * factor);
    };

    // 더블탭(클릭) — 핀치 직후 유령 클릭 억제
    let lastClick = 0;
    const onClick = (e: MouseEvent) => {
      const now = Date.now();
      if (now - lastPinchEnd < 650) return; // 핀치 끝난 직후 발생하는 click 무시 (여유 650ms)
      if (now - lastInteract < 200) return; // 막 인터랙션 후 곧바로 뜨는 click 무시

      if (now - lastClick < 300) {
        zoomAt(e.clientX, e.clientY, scaleRef.current > 1 ? 1 : 2);
      }
      lastClick = now;
    };

    vp.addEventListener("pointerdown", onDown as any, { passive: false });
    vp.addEventListener("pointermove", onMove as any, { passive: false });
    vp.addEventListener("pointerup", onUp as any, { passive: false });
    vp.addEventListener("pointercancel", onUp as any, { passive: false });
    vp.addEventListener("wheel", onWheel, { passive: false });
    vp.addEventListener("click", onClick);

    return () => {
      vp.removeEventListener("pointerdown", onDown as any);
      vp.removeEventListener("pointermove", onMove as any);
      vp.removeEventListener("pointerup", onUp as any);
      vp.removeEventListener("pointercancel", onUp as any);
      vp.removeEventListener("wheel", onWheel as any);
      vp.removeEventListener("click", onClick as any);
    };
  }, [open, minScale, maxScale]);

  if (!open) return null;

  return (
    <div className="zoom-modal" role="dialog" aria-modal="true" aria-label="이미지 뷰어">
      <div className="zoom-backdrop" onClick={onClose} />
      <div className="zoom-dialog">
        <div className="zoom-header">
          <div className="zoom-title">{alt || "이미지 확대 / 축소"}</div>
          <button className="zoom-iconbtn" onClick={onClose} aria-label="닫기">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>
        <div ref={vpRef} className="zoom-viewport">
          <img ref={imgRef} className="zoom-img" src={src} alt={alt || ""} />
        </div>
      </div>
    </div>
  );
}
